export const ALL_PET_TYPES_VALUE = 'all';

export type PetCategoryGroup =
  | 'companion'
  | 'equine'
  | 'small_mammals'
  | 'birds'
  | 'reptiles'
  | 'aquatic'
  | 'farm'
  | 'other';

export interface ContestPetTypeOption {
  code: string;
  group: PetCategoryGroup;
}

export const CONTEST_PET_TYPE_OPTIONS: ContestPetTypeOption[] = [
  { code: 'dog', group: 'companion' },
  { code: 'cat', group: 'companion' },
  { code: 'horse', group: 'equine' },
  { code: 'pony', group: 'equine' },
  { code: 'rabbit', group: 'small_mammals' },
  { code: 'guinea_pig', group: 'small_mammals' },
  { code: 'hamster', group: 'small_mammals' },
  { code: 'ferret', group: 'small_mammals' },
  { code: 'chinchilla', group: 'small_mammals' },
  { code: 'rat', group: 'small_mammals' },
  { code: 'mouse', group: 'small_mammals' },
  { code: 'hedgehog', group: 'small_mammals' },
  { code: 'bird', group: 'birds' },
  { code: 'parrot', group: 'birds' },
  { code: 'chicken', group: 'birds' },
  { code: 'duck', group: 'birds' },
  { code: 'turtle', group: 'reptiles' },
  { code: 'snake', group: 'reptiles' },
  { code: 'lizard', group: 'reptiles' },
  { code: 'fish', group: 'aquatic' },
  { code: 'goat', group: 'farm' },
  { code: 'sheep', group: 'farm' },
  { code: 'pig', group: 'farm' },
  { code: 'cow', group: 'farm' },
  { code: 'other', group: 'other' },
];

export const CONTEST_PET_GROUP_ORDER: PetCategoryGroup[] = [
  'companion',
  'equine',
  'small_mammals',
  'birds',
  'reptiles',
  'aquatic',
  'farm',
  'other',
];

const LEGACY_ALL_ALIASES = new Set([
  'all',
  'todas las mascotas',
  'all pet types',
  'all pets',
  'todos',
  'any',
]);

const LEGACY_PET_ALIASES: Record<string, string> = {
  perro: 'dog',
  perros: 'dog',
  gato: 'cat',
  gatos: 'cat',
  caballo: 'horse',
  caballos: 'horse',
  pajaro: 'bird',
  pájaro: 'bird',
  aves: 'bird',
  ave: 'bird',
  hamster: 'hamster',
  conejo: 'rabbit',
  'guinea pig': 'guinea_pig',
  'cuyo': 'guinea_pig',
  huron: 'ferret',
  hurón: 'ferret',
  pez: 'fish',
  peces: 'fish',
  tortuga: 'turtle',
  serpiente: 'snake',
  lagarto: 'lizard',
  cabra: 'goat',
  oveja: 'sheep',
  cerdo: 'pig',
  vaca: 'cow',
  otro: 'other',
  others: 'other',
};

export function getAllConfiguredPetTypeCodes(): string[] {
  return CONTEST_PET_TYPE_OPTIONS.map((pet) => pet.code);
}

export function normalizePetTypeCode(code: string): string {
  const trimmed = String(code || '').trim().toLowerCase();
  if (!trimmed) return '';
  if (LEGACY_ALL_ALIASES.has(trimmed)) return ALL_PET_TYPES_VALUE;
  return LEGACY_PET_ALIASES[trimmed] || trimmed;
}

export function parseAllowedPetTypes(raw: unknown): string[] {
  let values: unknown[] = [];

  if (Array.isArray(raw)) {
    values = raw;
  } else if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (!trimmed) {
      values = [];
    } else {
      try {
        const parsed = JSON.parse(trimmed);
        values = Array.isArray(parsed) ? parsed : [parsed];
      } catch {
        values = trimmed.includes(',')
          ? trimmed.split(',').map((part) => part.trim())
          : [trimmed];
      }
    }
  } else if (raw) {
    values = [raw];
  }

  const normalized = values.map((value) => normalizePetTypeCode(String(value))).filter(Boolean);
  if (normalized.length === 0) return [ALL_PET_TYPES_VALUE];
  if (normalized.includes(ALL_PET_TYPES_VALUE)) return [ALL_PET_TYPES_VALUE];
  return [...new Set(normalized)];
}

export function sanitizeAllowedPetTypes(raw: unknown): string[] {
  const parsed = parseAllowedPetTypes(raw);
  if (isAllPetTypesSelection(parsed)) return [ALL_PET_TYPES_VALUE];

  const validCodes = new Set(getAllConfiguredPetTypeCodes());
  const specific = parsed.filter((code) => validCodes.has(code));
  return specific.length > 0 ? specific : [ALL_PET_TYPES_VALUE];
}

export function isAllPetTypesSelection(petTypes: unknown): boolean {
  const parsed = parseAllowedPetTypes(petTypes);
  return parsed.length === 1 && parsed[0] === ALL_PET_TYPES_VALUE;
}

export function normalizePetTypeSelection(previous: string[], next: string[]): string[] {
  const prev = sanitizeAllowedPetTypes(previous);
  const selected = next.map((code) => normalizePetTypeCode(code)).filter(Boolean);

  if (selected.length === 0) return [ALL_PET_TYPES_VALUE];

  const added = selected.filter((code) => !prev.includes(code));

  if (added.includes(ALL_PET_TYPES_VALUE)) {
    return [ALL_PET_TYPES_VALUE];
  }

  if (prev.includes(ALL_PET_TYPES_VALUE) && selected.some((code) => code !== ALL_PET_TYPES_VALUE)) {
    return sanitizeAllowedPetTypes(selected.filter((code) => code !== ALL_PET_TYPES_VALUE));
  }

  const withoutAll = selected.filter((code) => code !== ALL_PET_TYPES_VALUE);
  return sanitizeAllowedPetTypes(withoutAll);
}

export function validateAllowedPetTypesSelection(petTypes: unknown): { valid: boolean; error?: string } {
  const parsed = sanitizeAllowedPetTypes(petTypes);
  if (parsed.length === 0) {
    return { valid: false, error: 'Select at least one pet type or All Pet Types' };
  }
  return { valid: true };
}

export function formatAllowedPetTypesLabel(
  petTypes: unknown,
  translate: (key: string) => string
): string {
  const parsed = sanitizeAllowedPetTypes(petTypes);
  if (isAllPetTypesSelection(parsed)) {
    return translate('eligibilityinfo.all_pet_types');
  }

  return parsed
    .map((code) => {
      const label = translate(`eligibilityinfo.${code}`);
      return label === `eligibilityinfo.${code}` ? code.replace(/_/g, ' ') : label;
    })
    .join(', ');
}

export function getPetTypesByGroup(group: PetCategoryGroup): ContestPetTypeOption[] {
  return CONTEST_PET_TYPE_OPTIONS.filter((pet) => pet.group === group);
}

export function isPetTypeEligible(petType: string, allowedPetTypes: unknown): boolean {
  if (isAllPetTypesSelection(allowedPetTypes)) return true;
  const allowed = sanitizeAllowedPetTypes(allowedPetTypes);
  const code = normalizePetTypeCode(petType);
  return code ? allowed.includes(code) : false;
}

export function validateParticipantAgeEligibility(
  age: number | null | undefined,
  ageGroup: string,
  minAge: number | null | undefined,
  maxAge: number | null | undefined
): { valid: boolean; error?: string } {
  if (age == null || Number.isNaN(Number(age))) {
    return { valid: true };
  }

  const participantAge = Number(age);

  if (ageGroup === 'under_18' && participantAge >= 18) {
    return { valid: false, error: 'This contest is limited to participants under 18' };
  }
  if (ageGroup === 'over_18' && participantAge < 18) {
    return { valid: false, error: 'This contest is limited to participants 18 and over' };
  }
  if (minAge != null && participantAge < Number(minAge)) {
    return { valid: false, error: `Minimum participant age is ${minAge}` };
  }
  if (maxAge != null && participantAge > Number(maxAge)) {
    return { valid: false, error: `Maximum participant age is ${maxAge}` };
  }

  return { valid: true };
}
